import { Outlet, createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { AppSidebar } from '@/components/layout/app-sidebar';
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useSession } from '@/features/auth/use-auth';
import { authApi } from '@/lib/api';
import { authStore } from '@/lib/auth-store';
import { isPlatformLocked } from '@/lib/platform-lock';
import { trackSessionActivity } from '@/lib/session-activity';
import { scheduleSessionExpiry } from '@/lib/session-expiry';
import { RouteErrorFallback } from '@/features/portal/ui';
import { useTranslation } from 'react-i18next';

function AppLayout() {
  const { t } = useTranslation('portal');
  const session = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // unified-access.md section 3.1 (UA-23): once the time left that the
  // server reported has passed and no save is in flight, ask the server
  // again. Only its 401 clears the session (the effect below then returns to
  // the login screen with this page); a still-valid answer re-arms the timer.
  // The device clock is never compared with the server's expiry time.
  const expiresInMs = session?.expiresInMs;
  const receivedAt = authStore.getReceivedAt();
  useEffect(
    () =>
      scheduleSessionExpiry(
        expiresInMs,
        receivedAt,
        () => void authStore.revalidate(),
        () => queryClient.isMutating() > 0,
      ),
    [expiresInMs, receivedAt, queryClient],
  );

  // unified-access.md section 3.1 (KAN-50, UA-28): the employee's clicks and
  // key presses keep the session open; reads and refetches never do.
  const signedIn = !!session;
  useEffect(
    () => (signedIn ? trackSessionActivity(authApi.recordActivity) : undefined),
    [signedIn],
  );

  useEffect(() => {
    // ui-contract.md §6: session expiry returns to the login screen and back
    // to the same page after signing in again.
    if (!session) {
      void navigate({
        to: '/login',
        search: { returnTo: globalThis.location.pathname + globalThis.location.search },
      });
    } else if (!session.selectedBranchId) {
      void navigate({ to: '/select-branch' });
    }
  }, [session, navigate]);

  if (!session || !session.selectedBranchId) return null;

  return (
    <SidebarProvider>
      <AppSidebar user={session.employee} />
      <SidebarInset className="min-w-0">
        <header className="flex h-16 shrink-0 items-center gap-2 px-4">
          <Tooltip>
            <TooltipTrigger asChild>
              <SidebarTrigger className="-ms-1" />
            </TooltipTrigger>
            <TooltipContent side="bottom">{t('nav.toggleSidebarHint')}</TooltipContent>
          </Tooltip>
        </header>
        <main className="flex min-w-0 flex-1 flex-col gap-6 p-4 pt-0 md:p-6 md:pt-0">
          <Outlet />
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}

export const Route = createFileRoute('/_app')({
  beforeLoad: async ({ location }) => {
    // unified-access.md section 3.2: locked sign-in never shows the portal.
    // The login screen returns a signed-in employee to the locked platform,
    // or signs them out with the reason when they may not open it.
    if (isPlatformLocked()) throw redirect({ to: '/login' });
    if (!(await authStore.restore()))
      throw redirect({
        to: '/login',
        search: { returnTo: location.pathname + location.searchStr },
      });
    if (!authStore.getSession()?.selectedBranchId) throw redirect({ to: '/select-branch' });
  },
  component: AppLayout,
  errorComponent: ({ error, reset }) => (
    <RouteErrorFallback error={error} reset={reset} boundary="app-shell" />
  ),
});
